import type { FC } from 'react';
import { useTheme } from '@mui/material/styles';
import { color, diagramStyle } from './palette';

/**
 * The deploy topology: three GCP projects, each Cloud Run over Postgres. Preview shares one database with a branch
 * database per PR; US and UK prod are separate regions with nothing between them.
 */
export const DeployTopology: FC = () => {
  const theme = useTheme();
  const sub = { fill: color.muted, fontSize: 13 } as const;
  const lbl = { fontSize: 15, fontWeight: 600 } as const;
  const row = { fontSize: 14, fill: color.fg } as const;
  const cylinder = (cx: number, cy: number, w: number, h: number, text: string) => (
    <g key={`db-${cx}`}>
      <path d={`M${cx - w / 2},${cy - h / 2 + 8} v${h - 16} a${w / 2},8 0 0 0 ${w},0 v-${h - 16}`} fill={color.silver} fillOpacity=".12" stroke={color.silver} strokeWidth="2" />
      <ellipse cx={cx} cy={cy - h / 2 + 8} rx={w / 2} ry="8" fill={color.paper} stroke={color.silver} strokeWidth="2" />
      <text x={cx} y={cy + 9} textAnchor="middle" style={row}>
        {text}
      </text>
    </g>
  );
  const cloudRun = (x: number, w: number, text: string) => (
    <g key={`run-${x}`}>
      <rect x={x} y="95" width={w} height="36" rx="18" fill="none" stroke={color.accent} strokeWidth="2" />
      <text x={x + w / 2} y="118" textAnchor="middle" style={row}>
        {text}
      </text>
    </g>
  );
  const project = (x: number, w: number, name: string, region: string) => (
    <g key={name}>
      <rect x={x} y="30" width={w} height="270" rx="14" fill="none" stroke={color.muted} strokeWidth="2.5" />
      <text x={x + 20} y="58" style={lbl} fill={color.fg}>
        {name}
      </text>
      <text x={x + 20} y="78" style={sub}>
        {region}
      </text>
    </g>
  );
  const branches = ['pr-101', 'pr-102', 'pr-103', 'pr-104', '…'];

  return (
    <svg viewBox="0 0 940 320" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="The deploy topology" style={diagramStyle(theme)}>
      {/* Preview: one project, one shared database, a branch database per PR */}
      {project(20, 340, 'preview', 'one GCP project')}
      {cloudRun(40, 300, 'Cloud Run · one per PR')}
      {cylinder(190, 170, 180, 52, 'one shared Postgres')}
      {branches.map((name, i) => {
        const x = 40 + i * 62;
        return (
          <g key={name}>
            <line x1="190" y1="196" x2={x + 27} y2="238" stroke={color.muted} strokeWidth="1.5" />
            <rect x={x} y="240" width="54" height="28" rx="4" fill={color.silver} opacity=".18" />
            <text x={x + 27} y="259" textAnchor="middle" fontSize="13" fill={color.fg}>
              {name}
            </text>
          </g>
        );
      })}
      <text x="190" y="288" textAnchor="middle" style={sub}>
        branch databases, one per PR
      </text>
      {/* Prod: two regions, nothing between them */}
      {project(390, 240, 'prod · US', 'us-central1 · own VPC')}
      {cloudRun(410, 200, 'Cloud Run')}
      {cylinder(510, 190, 180, 52, 'Postgres')}
      <text x="510" y="250" textAnchor="middle" style={sub}>
        its own instance
      </text>
      {project(680, 240, 'prod · UK', 'europe-west2 · own VPC')}
      {cloudRun(700, 200, 'Cloud Run')}
      {cylinder(800, 190, 180, 52, 'Postgres')}
      <text x="800" y="250" textAnchor="middle" style={sub}>
        its own instance
      </text>
      <text x="655" y="170" textAnchor="middle" fontSize="13" fontWeight="600" fill={color.warn} transform="rotate(-90 655 170)">
        never peered
      </text>
    </svg>
  );
};
